import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AuthContextType, User } from '../types';
import authService from '../services/auth';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Verificar se há um token válido ao carregar a aplicação
    const initAuth = async () => {
      const storedToken = authService.getToken();
      
      if (storedToken) {
        setToken(storedToken);
        const userData = await authService.validateToken();
        
        if (userData) {
          setUser(userData);
        } else {
          // Token inválido, limpar tudo
          setToken(null);
          sessionStorage.removeItem('psd_token');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authService.login(email, password);
      
      if (response.success && response.token) {
        setToken(response.token);
        
        // Validar o token e obter dados do usuário
        const userData = await authService.validateToken();
        
        if (userData) {
          setUser(userData);
        } else {
          // Se não conseguiu validar, usar dados temporários
          setUser({ account_id: 'temp-' + Date.now(), email: email });
        }
        
        return { success: true };
      }
      
      // Se login falhou, garantir que user e token estejam limpos
      setUser(null);
      setToken(null);
      return { success: false, error: response.error || 'Credenciais inválidas' };
    } catch (error) {
      console.error('Erro no AuthContext.login:', error);
      // Garantir que user e token estejam limpos em caso de erro
      setUser(null);
      setToken(null);
      return { success: false, error: 'Erro interno. Tente novamente.' };
    }
  };

  const logout = async (): Promise<void> => {
    setLoading(true);
    try {
      await authService.logout();
    } finally {
      setUser(null);
      setToken(null);
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!user && !!token,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};